<template>

    <Toast/>

    <MenuMain/>
    <MenuPmModuleOverview v-if="(this.userStore.user.roles.includes('ROLE_ISOPRACTICE', 0))"/>
    <MenuMsModuleOverview v-else-if="(this.userStore.user.roles.includes('ROLE_ISOEVOLVE', 0))"/>
    <HeadingOverviewPlanning/>

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Planning</h3>

                    <div class="field">
                        <label for="manualClientIntakeForms">Do You Have Manual Client Intake Forms (ie. not in
                            IsoEvolve currently)?</label>
                        <Dropdown v-model="companyConfiguration.manualClientIntakeForms" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div class="field">
                        <label for="manualBillingInstructions">Do You Have Manual Billing Instructions (ie. not in
                            IsoEvolve currently)?</label>
                        <Dropdown v-model="companyConfiguration.manualBillingInstructions" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <div class="field">
                        <label for="weeklyNewClients">Average Number of New Clients Per Week?</label>
                        <InputNumber id="horizontal" v-model="companyConfiguration.weeklyNewClients" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     :max="200"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="weeklyWorkingDays">Number of Working Days Per Week?</label>
                        <InputNumber id="horizontal" v-model="companyConfiguration.weeklyWorkingDays" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     :max="7"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="weeklyBillableAppointments">Average Number of Billable Appointments Per
                            Week?</label>
                        <InputNumber id="horizontal" v-model="companyConfiguration.weeklyBillableAppointments" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     :max="500"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="clientSplit">Percentage of Private Clients vs Health Insurance Clients?</label>
                        <InputNumber id="horizontal" v-model="companyConfiguration.clientSplit" showButtons
                                     buttonLayout="horizontal"
                                     :step="10"
                                     :min="0"
                                     :max="100"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="monthlyBankStatements">Number of Bank Statements Received Per Month?</label>
                        <InputNumber id="horizontal" v-model="companyConfiguration.monthlyBankStatements" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     :max="200"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="bankStatementSize">Average Number of Pages Per Bank Statement?</label>
                        <InputNumber id="horizontal" v-model="companyConfiguration.bankStatementSize" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     :max="200"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="monthlyRemittances">Number of Healthcare Remittances Received Per Month?</label>
                        <InputNumber id="horizontal" v-model="companyConfiguration.monthlyRemittances" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     :max="200"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="remittanceSize">Average Number of Pages Per Remittance?</label>
                        <InputNumber id="horizontal" v-model="companyConfiguration.remittanceSize" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     :max="200"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <Button label="Save and Calculate" class="mr-2 mb-2" @click="updateConfiguration"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Calculation of Time Usage Required Per Month</h3>

                    <h4>Assumptions</h4>
                    <p>
                        1. Each manual client intake form requires {{ this.timeAllocation.timeClientIntakeProcessing }} minutes to complete.
                        <br>
                        2. Each billing instruction requires {{ this.timeAllocation.timeBillingInstructionProcessing }} minutes to complete.
                        <br>
                        3. Each invoice requires {{ this.timeAllocation.timeInvoiceProcessing }} minutes to complete.
                        <br>
                        4. Each invoice requires {{ this.timeAllocation.timeUnpaidsProcessing }} minutes of unpaids management to complete.
                        <br>
                        5. Each Health Insurance Follow-Up requires {{ this.timeAllocation.timeHealthInsuranceFollowups }} minutes to complete.
                        <br>
                        6. Each Page of a Health Insurance Remittance requires {{ this.timeAllocation.timeHealthInsuranceRemittances }} minutes to complete.
                        <br>
                        7. Each Page of a Bank Statement requires {{ this.timeAllocation.timePaymentAllocations }} minutes to complete.
                    </p>

                    <p>
                        <br>
                    </p>

                    <h4>Current Workload</h4>
                    <p>
                        Weekly New Clients = {{ this.companyConfiguration.weeklyNewClients }}
                        <br>
                        Weekly Billable Appointments = {{ this.companyConfiguration.weeklyBillableAppointments }}
                        <br>
                        Client Split = {{ this.companyConfiguration.clientSplit }}
                        <br>
                        Monthly Bank Statements = {{ this.companyConfiguration.monthlyBankStatements }}
                        <br>
                        Average Bank Statement (in pages) = {{ this.companyConfiguration.bankStatementSize }}
                        <br>
                        Monthly Health Insurance Remittances = {{ this.companyConfiguration.monthlyRemittances }}
                        <br>
                        Average Health Insurance Remittance (in pages) = {{ this.companyConfiguration.remittanceSize }}
                    </p>

                    <p>
                        <br>
                    </p>

                    <h4>Recommendations for Your Monthly Operational Work Usage</h4>
                    <p>

                        Client Intake Processing = {{ formatTime(this.calculation.clientIntakeProcessing) }}
                        <br>
                        Billing Instruction Processing = {{ formatTime(this.calculation.billingInstructionProcessing) }}
                        <br>
                        Invoice Processing = {{ formatTime(this.calculation.invoiceProcessing) }}
                        <br>
                        Unpaids Processing = {{ formatTime(this.calculation.unpaidsProcessing) }}
                        <br>
                        Health Insurance Follow-Ups = {{ formatTime(this.calculation.healthInsuranceFollowups) }}
                        <br>
                        Health Insurance Remittances = {{ formatTime(this.calculation.healthInsuranceRemittances) }}
                        <br>
                        Payment Allocations (from Bank Statements) ={{ formatTime(this.calculation.paymentAllocations) }}
                        <br><br>
                        <B>TOTAL</B> = {{ formatTime(this.calculation.total) }}

                    </p>

                    <Button label="Back" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuPmModuleOverview from "@/components/menu/pmmodule/MenuPmModuleOverview.vue";
import MenuMsModuleOverview from "@/components/menu/msmodule/MenuMsModuleOverview.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import MenuMain from "@/components/menu/common/MenuMain.vue";
import HeadingOverviewPlanning from "@/components/heading/common/HeadingOverviewPlanning.vue";
import CompanyService from "@/service/CompanyService";

export default {
    components: {
        HeadingOverviewPlanning,
        MenuMain,
        MenuPmModuleOverview,
        MenuMsModuleOverview,
        IsoEvolveFooter
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    async mounted() {
        try {
            // Attempt to get user data
            const userData = await UserService.getUser(this.userStore.user.id);
            this.profile = userData.data;
            console.log("[PhoneCalls][mounted] Data = ", userData);
            console.log("[PhoneCalls][mounted] this.profile = ", this.profile);
        } catch (e) {
            console.error("[PhoneCalls][mounted] Error fetching user data = ", e);
            this.userStore.clearUser();
            this.$router.push('login');
            return; // Exit early if getUser fails
        }

        try {
            const [userConfigData] = await Promise.all([
                UserService.getUserConfig(this.userStore.user.id)
            ]);

            this.userConfiguration = userConfigData.data;
            console.log("[PhoneCalls][mounted] userConfigData.data = ", userConfigData.data);
        } catch (e) {
            console.error("[PhoneCalls][mounted] Could not find user config for user = ", this.userStore.user.id);
        }

        try {
            const [companyConfigData] = await Promise.all([
                CompanyService.getCompanyConfiguration(this.profile.companyId)
            ]);

            this.companyConfiguration = companyConfigData.data;
            console.log("[PhoneCalls][mounted] companyConfigData.data = ", companyConfigData.data);

            if (this.companyConfiguration.manualClientIntakeForms == null) {
                this.companyConfiguration.manualClientIntakeForms = 'Y';
            }

            if (this.companyConfiguration.manualBillingInstructions == null) {
                this.companyConfiguration.manualBillingInstructions = 'Y';
            }

            if (this.companyConfiguration.weeklyNewClients == null) {
                this.companyConfiguration.weeklyNewClients = 1;
            }

            if (this.companyConfiguration.weeklyWorkingDays == null) {
                this.companyConfiguration.weeklyWorkingDays = 4;
            }

            if (this.companyConfiguration.weeklyBillableAppointments == null) {
                this.companyConfiguration.weeklyBillableAppointments = 3;
            }

            if (this.companyConfiguration.clientSplit == null) {
                this.companyConfiguration.clientSplit = 80;
            }

            if (this.companyConfiguration.monthlyBankStatements == null) {
                this.companyConfiguration.monthlyBankStatements = 1;
            }

            if (this.companyConfiguration.bankStatementSize == null) {
                this.companyConfiguration.bankStatementSize = 3;
            }

            if (this.companyConfiguration.monthlyRemittances == null) {
                this.companyConfiguration.monthlyRemittances = 4;
            }

            if (this.companyConfiguration.remittanceSize == null) {
                this.companyConfiguration.remittanceSize = 2;
            }

            this.loading = false;

        } catch (e) {
            console.error("[PhoneCalls][mounted] Error fetching additional data = ", e);
        }

        console.log("[PhoneCalls][mounted] this.userConfiguration = ", this.userConfiguration);
        console.log("[PhoneCalls][mounted] this.companyConfiguration = ", this.companyConfiguration);
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            calculation: {},
            timeAllocation: {},
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ]
        }
    },
    methods: {
        callReset() {
          this.calculation = {};
          this.timeAllocation = {};
          this.activeTab = 1;
        },
        formatTime(minutes) {
            console.log("[" + name + "][callBack] minutes = ", minutes);
            const days = Math.floor(minutes / (60 * 24));
            minutes -= days * 60 * 24;

            console.log("[" + name + "][callBack] minutes = ", minutes);

            const hours = Math.floor(minutes / 60);
            minutes -= hours * 60;

            console.log("[" + name + "][callBack] minutes = ", minutes);

            const formattedMinutes = minutes.toFixed(0);

            let formattedTime = "";

            if (days > 0) {
                formattedTime += `${days} day${days > 1 ? 's' : ''} `;
            }

            if (hours > 0) {
                formattedTime += `${hours} hr${hours > 1 ? 's' : ''} `;
            }

            if (minutes > 0) {
                formattedTime += `${formattedMinutes} min${minutes > 1 ? 's' : ''} `;
            } else {
                formattedTime += "0 min"
            }

            return formattedTime;
        },
        async updateConfiguration() {

            var updatedConfiguration = {
                manualClientIntakeForms: this.companyConfiguration.manualClientIntakeForms,
                manualBillingInstructions: this.companyConfiguration.manualBillingInstructions,
                weeklyNewClients: this.companyConfiguration.weeklyNewClients,
                weeklyWorkingDays: this.companyConfiguration.weeklyWorkingDays,
                weeklyBillableAppointments: this.companyConfiguration.weeklyBillableAppointments,
                clientSplit: this.companyConfiguration.clientSplit,
                monthlyBankStatements: this.companyConfiguration.monthlyBankStatements,
                bankStatementSize: this.companyConfiguration.bankStatementSize,
                monthlyRemittances: this.companyConfiguration.monthlyRemittances,
                remittanceSize: this.companyConfiguration.remittanceSize
            };

            this.timeAllocation.timeClientIntakeProcessing = 5;
            this.timeAllocation.timeBillingInstructionProcessing = 30;
            this.timeAllocation.timeInvoiceProcessing = 2;
            this.timeAllocation.timeUnpaidsProcessing = 5;
            this.timeAllocation.timeHealthInsuranceFollowups = 15;
            this.timeAllocation.timeHealthInsuranceRemittances = 5;
            this.timeAllocation.timePaymentAllocations = 5;

            if (this.companyConfiguration.manualBillingInstructions === "Y") {
                this.calculation.billingInstructionProcessing = 4 * this.timeAllocation.timeBillingInstructionProcessing;
            } else {
                this.calculation.billingInstructionProcessing = 0;
            }

            this.calculation.clientIntakeProcessing = this.companyConfiguration.weeklyNewClients * 4 * this.timeAllocation.timeClientIntakeProcessing;
            this.calculation.invoiceProcessing = this.companyConfiguration.weeklyBillableAppointments * 4 * this.timeAllocation.timeInvoiceProcessing;
            this.calculation.unpaidsProcessing = this.companyConfiguration.weeklyBillableAppointments * 4 * this.timeAllocation.timeUnpaidsProcessing;
            this.calculation.healthInsuranceFollowups = ((this.companyConfiguration.weeklyBillableAppointments * ((100 - this.companyConfiguration.clientSplit) / 100.0)) * 4 * this.timeAllocation.timeHealthInsuranceFollowups);
            this.calculation.healthInsuranceRemittances = this.companyConfiguration.monthlyRemittances * this.companyConfiguration.remittanceSize * this.timeAllocation.timeHealthInsuranceRemittances;
            this.calculation.paymentAllocations = this.companyConfiguration.monthlyBankStatements * this.companyConfiguration.bankStatementSize * this.timeAllocation.timePaymentAllocations;

            this.calculation.total = this.calculation.clientIntakeProcessing + this.calculation.billingInstructionProcessing + this.calculation.invoiceProcessing + this.calculation.unpaidsProcessing + this.calculation.healthInsuranceFollowups + this.calculation.healthInsuranceRemittances + this.calculation.paymentAllocations;

            console.log("[" + name + "][processUpdateConfiguration] Updated configuration. Data = ", updatedConfiguration);
            console.log("[" + name + "][processUpdateConfiguration] Updated calculation. Data = ", this.calculation);

            this.activeTab = 2;

            try {
                // Delete the appointment
                const response = await CompanyService.updateCompanyConfiguration(this.profile.companyId, updatedConfiguration);
                console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                const [companyConfigData] = await Promise.all([
                    CompanyService.getCompanyConfiguration(this.profile.companyId)
                ]);

                this.companyConfiguration = companyConfigData.data;
                console.log("[PhoneCalls][mounted] companyConfigData.data = ", companyConfigData.data);

                if (this.companyConfiguration.manualClientIntakeForms == null) {
                    this.companyConfiguration.manualClientIntakeForms = 'Y';
                }

                if (this.companyConfiguration.manualBillingInstructions == null) {
                    this.companyConfiguration.manualBillingInstructions = 'Y';
                }

                if (this.companyConfiguration.weeklyNewClients == null) {
                    this.companyConfiguration.weeklyNewClients = 1;
                }

                if (this.companyConfiguration.weeklyWorkingDays == null) {
                    this.companyConfiguration.weeklyWorkingDays = 4;
                }

                if (this.companyConfiguration.weeklyBillableAppointments == null) {
                    this.companyConfiguration.weeklyBillableAppointments = 3;
                }

                if (this.companyConfiguration.clientSplit == null) {
                    this.companyConfiguration.clientSplit = 80;
                }

                if (this.companyConfiguration.monthlyBankStatements == null) {
                    this.companyConfiguration.monthlyBankStatements = 1;
                }

                if (this.companyConfiguration.bankStatementSize == null) {
                    this.companyConfiguration.bankStatementSize = 3;
                }

                if (this.companyConfiguration.monthlyRemittances == null) {
                    this.companyConfiguration.monthlyRemittances = 4;
                }

                if (this.companyConfiguration.remittanceSize == null) {
                    this.companyConfiguration.remittanceSize = 2;
                }

                this.$toast.add({
                    severity: 'success',
                    summary: 'Successful',
                    detail: 'Configuration updated',
                    life: 3000
                });

            } catch (error) {
                console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update configuration. Please try again.',
                    life: 3000
                });
            }

        }
    }
};
</script>

<style scoped>
a {
    text-decoration: none;
}
</style>
